import React from "react";
import Table from "../../../components/molecules/Table";
import { usePermissionAccess } from "../../../system/hooks/usePermissionAccess";
import { ReactComponent as LockIcon } from "../../../images/icons/elements/unlocked.svg";
import { ReactComponent as LockActiveIcon } from "../../../images/icons/elements/unlocked-active.svg";
import classNames from "classnames";
import {
  useHideModal,
  useShowModal,
} from "../../../components/organisms/Modal/api/hooks";
import { modalType } from "../../../components/organisms/Modal/constants";
import MaliciousIpItemTable from "./MaliciousIpItemTable";
import TableModal from "../../../components/molecules/TableModal";
import { maliciousIpsAccess } from "../constants";
import { Tooltip } from "antd";
import { checkIsDayBefore } from "../helpers";

const MaliciousIpTable = ({
  data,
  isLoading,
  setPage,
  currentPage,
  handleReleaseIp,
}) => {
  const { checkPermission } = usePermissionAccess();

  const accessShowControl = checkPermission(maliciousIpsAccess.show);
  const accessReleaseControl = checkPermission(maliciousIpsAccess.release);

  const cellContent = ({ text, className = "", onClick = null }) => (
    <div onClick={onClick} className={classNames("simple-field", className)}>
      <span>{text}</span>
    </div>
  );

  const cellTooltip = ({
    text,
    className = "",
    onClick = null,
    accessControl,
  }) => (
    <Tooltip
      title={!accessControl && "You do not have permission for that operation."}
    >
      {cellContent({ text, className, onClick })}
    </Tooltip>
  );

  const cellLink = ({ text, record, onClick = null }) => {
    return (
      <span key={record?.id}>
        {cellContent({
          text,
          className: "field-link",
          onClick,
        })}
      </span>
    );
  };

  const { handleOnClose } = useHideModal();

  const maliciousIpModal = (): modalType => ({
    wrapClassName: "table-modal",
    containerClass: "table-modal__container",
    customRender: TableModal,
    width: 600,
    maskClosable: false,
    componentProps: {
      onClose: handleOnClose,
    },
  });

  const tableModalHandler = useShowModal(maliciousIpModal());

  const showTableModal = (record) => {
    const isDayBefore = checkIsDayBefore(record.blocked_until);
    tableModalHandler({
      item: record,
      modalContent: <MaliciousIpItemTable ip={record.id} />,
      onApply: () =>
        !isDayBefore && accessReleaseControl && handleReleaseIp(record.id),
      customApplyIcon:
        isDayBefore || !accessReleaseControl ? (
          <Tooltip
            title={
              !accessReleaseControl &&
              "You do not have permission for that operation."
            }
          >
            <LockIcon />
          </Tooltip>
        ) : (
          <LockActiveIcon />
        ),
      additionalClassName:
        (isDayBefore || !accessReleaseControl) &&
        "table-modal__footer-apply-disable",
      title: record.ip,
    });
  };

  const colData = [
    {
      title: "IP",
      width: 120,
      dataIndex: "ip",
      render: (text, record) => {
        return !accessShowControl
          ? cellContent({ text })
          : cellLink({
              text,
              record,
              onClick: () => showTableModal(record),
            });
      },
    },
    {
      title: "Blocked at",
      dataIndex: "block_reason",
      width: 120,
      render: (text) => cellContent({ text }),
    },
    {
      title: "Date blocked",
      dataIndex: "blocked_at",
      render: (text) => cellContent({ text }),
    },
    {
      title: "Blocked",
      width: 100,
      dataIndex: "block_count",
      render: (text) => cellContent({ text }),
    },
    {
      title: "Released at",
      dataIndex: "blocked_until",
      render: (text) => cellContent({ text: text || "Permanent" }),
    },
    {
      title: "Actions",
      width: 60,
      render: (text, record) => {
        const isDayBefore = checkIsDayBefore(record.blocked_until);
        return cellTooltip({
          text:
            isDayBefore || !accessReleaseControl ? (
              <LockIcon />
            ) : (
              <LockActiveIcon />
            ),
          className: classNames("field-icon", {
            "field-icon-disabled": isDayBefore || !accessReleaseControl,
          }),
          onClick: () =>
            !isDayBefore && accessReleaseControl && handleReleaseIp(record.id),
          accessControl: accessReleaseControl,
        });
      },
    },
  ];

  return (
    <Table
      className="table__malicious-ip-list grid-table"
      colData={colData}
      rowData={data?.data.map((item, i) => ({
        key: i + 1,
        ...item,
      }))}
      paginationSettings={{
        total: data?.total,
        pageSize: data?.per_page,
        current: currentPage,
        showQuickJumper: true,
        showSizeChanger: false,
      }}
      scrollWidth={600}
      scrollHeight={0}
      setPage={setPage}
      loading={isLoading}
    />
  );
};

export default MaliciousIpTable;
